// Docked Inspector: shows the selected clip's settings (timing, speed, volume,
// fades, transform/crop, audio processing) in the left panel, live — no modal.
// Re-renders when the selection changes, and (debounced) when the clip changes
// elsewhere, but never while you're dragging one of its own controls.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { el } from "../util";
import { buildClipInspector } from "./clip-inspector";

export class InspectorPanel {
  el = el("div", { class: "inspector" });
  private title = el("div", { class: "insp-title" });
  private body = el("div", { class: "insp-body fx-editor" });
  private clipId: string | null = null;
  private render: (() => void) | null = null;
  private interacting = false;
  private pending = false;
  private timer: any = null;
  visible = false;

  constructor(private store: Store, private engine: AudioEngine) {
    this.el.append(this.title, this.body);
    store.on("selection", () => this.refresh(true));
    store.on("change", ({ scope }) => { if (scope === "tracks" || scope === "project" || scope === "assets") this.refresh(false); });
    // Don't rebuild under the pointer mid-drag of a slider; catch up on release.
    this.body.addEventListener("pointerdown", () => { this.interacting = true; });
    window.addEventListener("pointerup", () => {
      if (!this.interacting) return;
      this.interacting = false;
      if (this.pending) this.refresh(false);
    });
    this.refresh(true);
  }

  setVisible(v: boolean) {
    this.visible = v;
    if (v) this.refresh(true);
  }

  private refresh(selectionChanged: boolean) {
    if (!this.visible) return;
    const id = this.store.selectedClipId && this.store.findClip(this.store.selectedClipId) ? this.store.selectedClipId : null;
    if (id !== this.clipId || selectionChanged) { clearTimeout(this.timer); this.rebuild(id); return; }
    if (!id) return;
    // Same clip changed (e.g. moved on the timeline): debounce, and wait while a
    // control in here has focus or is being dragged.
    const busy = this.interacting || this.body.contains(document.activeElement);
    if (busy) { this.pending = true; return; }
    this.pending = false;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { if (!this.interacting && !this.body.contains(document.activeElement)) this.render?.(); }, 150);
  }

  private rebuild(id: string | null) {
    this.clipId = id;
    this.body.innerHTML = "";
    this.render = null;
    if (!id) {
      this.title.textContent = "Inspector";
      this.body.append(el("p", { class: "hint insp-empty", text: "Select a clip on the timeline to see and edit its settings here." }));
      return;
    }
    const f = this.store.findClip(id)!;
    const n = this.store.selectedClipIds.size;
    this.title.textContent = `${f.track.kind === "video" ? "🎬" : "🎵"} ${f.clip.name}${n > 1 ? `  (+${n - 1} selected)` : ""}`;
    this.render = buildClipInspector(this.store, this.engine, id, this.body);
  }
}
